import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, TextField, MenuItem, CircularProgress, Alert,
  Stack, Breadcrumbs, Link, Avatar, Chip, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Divider
} from '@mui/material';
import { 
  EventNote, AccessTime, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Refresh, School
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../config/api';

const DAYS = [
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
];

const SUBJECT_COLORS = [
  '#4C51BF', '#38A169', '#D69E2E', '#E53E3E', '#3182CE',
  '#805AD5', '#DD6B20', '#319795', '#2D3748', '#C05621'
];

const TimetablePage = () => {
  const navigate = useNavigate();
  const [slots, setSlots] = useState<any[]>([]);
  const [classGroups, setClassGroups] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSlot, setEditingSlot] = useState<any>(null);

  const [formData, setFormData] = useState({
    dayOfWeek: 1,
    startTime: '08:00',
    endTime: '09:00',
    subjectId: '',
    title: '',
    type: 'LESSON', // 'LESSON' or 'BREAK'
    teacherId: ''
  });

  const getHeaders = () => {
    const userInfo = localStorage.getItem('userInfo');
    const token = userInfo ? JSON.parse(userInfo).token : null;
    return { Authorization: `Bearer ${token}` };
  };

  // Load class groups (role-filtered, same as attendance page)
  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(getApiUrl('groups/my'), { headers: getHeaders() });
      const groups = res.data || [];
      setClassGroups(groups);
      if (groups.length > 0) setSelectedGroupId(groups[0].id);
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  }, []);

  // Load subjects for the dropdown
  const fetchSubjects = useCallback(async () => {
    try {
      const res = await axios.get(getApiUrl('subjects'), { headers: getHeaders() });
      setSubjects(res.data || []);
    } catch (err) {
      console.error('Error fetching subjects:', err);
    }
  }, []);

  // Load timetable slots for selected group
  const fetchTimetable = useCallback(async () => {
    if (!selectedGroupId) return;
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(getApiUrl(`timetable?classGroupId=${selectedGroupId}`), { headers: getHeaders() });
      setSlots(res.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load timetable');
    } finally {
      setLoading(false);
    }
  }, [selectedGroupId]);

  useEffect(() => {
    fetchGroups();
    fetchSubjects();
  }, [fetchGroups, fetchSubjects]);

  useEffect(() => {
    fetchTimetable();
  }, [fetchTimetable]);

  const openAddDialog = (day?: number) => {
    setEditingSlot(null);
    setFormData({
      dayOfWeek: day || 1,
      startTime: '08:00',
      endTime: '09:00',
      subjectId: subjects[0]?.id || '',
      title: '',
      type: 'LESSON',
      teacherId: ''
    });
    setOpenDialog(true);
  };

  const openEditDialog = (slot: any) => {
    setEditingSlot(slot);
    setFormData({
      dayOfWeek: slot.dayOfWeek,
      startTime: slot.startTime,
      endTime: slot.endTime,
      subjectId: slot.subjectId || '',
      title: slot.title || '',
      type: slot.subjectId ? 'LESSON' : 'BREAK',
      teacherId: slot.teacherId || ''
    });
    setOpenDialog(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        subjectId: formData.type === 'LESSON' ? formData.subjectId : null,
        title: formData.type === 'BREAK' ? formData.title : null,
      };

      if (editingSlot) {
        await axios.put(getApiUrl(`timetable/${editingSlot.id}`), payload, { headers: getHeaders() });
        setSuccessMsg('Slot updated successfully');
      } else {
        await axios.post(getApiUrl('timetable'), {
          ...payload,
          classGroupId: selectedGroupId
        }, { headers: getHeaders() });
        setSuccessMsg('Slot added successfully');
      }
      setOpenDialog(false);
      fetchTimetable();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save timetable slot');
    }
  };

  const handleDelete = async (slotId: string) => {
    if (!window.confirm('Remove this timetable slot?')) return;
    try {
      await axios.delete(getApiUrl(`timetable/${slotId}`), { headers: getHeaders() });
      fetchTimetable();
      setSuccessMsg('Slot removed');
      setTimeout(() => setSuccessMsg(''), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete slot');
    }
  };

  // Build a color map keyed by subjectId
  const subjectColorMap: Record<string, string> = {};
  subjects.forEach((s, i) => {
    subjectColorMap[s.id] = SUBJECT_COLORS[i % SUBJECT_COLORS.length];
  });

  const selectedGroup = classGroups.find(g => g.id === selectedGroupId);

  // Build timetable grid data: { [dayValue]: slots[] }
  const slotsByDay: Record<number, any[]> = {};
  DAYS.forEach(d => { slotsByDay[d.value] = []; });
  slots.forEach(slot => {
    if (slotsByDay[slot.dayOfWeek]) slotsByDay[slot.dayOfWeek].push(slot);
  });

  return (
    <Box sx={{ p: 4, bgcolor: '#F7FAFC', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs sx={{ mb: 1, fontSize: '0.85rem' }}>
          <Link underline="hover" color="inherit" onClick={() => navigate('/dashboard')} sx={{ cursor: 'pointer' }}>Dashboard</Link>
          <Link underline="hover" color="inherit" onClick={() => navigate('/dashboard/academics')} sx={{ cursor: 'pointer' }}>Academics</Link>
          <Typography color="text.primary">Timetable</Typography>
        </Breadcrumbs>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h3" fontWeight={800} sx={{ color: '#263B7C', letterSpacing: '-0.02em', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
              Class Timetable
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              Visual weekly schedule management for {selectedGroup?.name || 'all classes'}.
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<Refresh />} onClick={fetchTimetable}
              sx={{ borderRadius: '12px', fontWeight: 700, borderColor: '#E2E8F0', color: '#4A5568', bgcolor: 'white' }}>
              Refresh
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openAddDialog()}
              sx={{ bgcolor: '#263B7C', borderRadius: '12px', px: 3, fontWeight: 700, '&:hover': { bgcolor: '#1e2e60' } }}>
              Add Slot
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Group Selector + Stats */}
      <Paper sx={{ p: 3, borderRadius: '20px', border: '1px solid #edf2f7', mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
            <TextField
              select label="Class Group" size="small"
              value={selectedGroupId}
              onChange={e => setSelectedGroupId(e.target.value)}
              sx={{ minWidth: 220, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            >
              {classGroups.map(g => (
                <MenuItem key={g.id} value={g.id}>
                  {g.name} {g.teacher?.name ? `— ${g.teacher.name}` : ''}
                </MenuItem>
              ))}
            </TextField>
            {selectedGroup && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: '#4C51BF15', color: '#4C51BF', width: 36, height: 36, borderRadius: '10px' }}>
                  <School sx={{ fontSize: 18 }} />
                </Avatar>
                <Box>
                  <Typography variant="caption" fontWeight={800} color="text.secondary">ASSIGNED TEACHER</Typography>
                  <Typography variant="body2" fontWeight={800} color="#2D3748">{selectedGroup.teacher?.name || 'Not assigned'}</Typography>
                </Box>
              </Stack>
            )}
          </Stack>
          <Stack direction="row" spacing={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={800} color="#263B7C">{slots.length}</Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>Total Slots</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={800} color="#38A169">{new Set(slots.map(s => s.dayOfWeek)).size}</Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>Active Days</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={800} color="#805AD5">{new Set(slots.map(s => s.subjectId)).size}</Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>Subjects</Typography>
            </Box>
          </Stack>
        </Stack>
      </Paper>

      {/* Alerts */}
      {successMsg && <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }}>{successMsg}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setError('')}>{error}</Alert>}

      {/* Weekly Grid */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 10 }}><CircularProgress /></Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(5, 1fr)' }, gap: 3 }}>
          {DAYS.map(day => {
            const daySlots = slotsByDay[day.value] || [];
            return (
              <Paper key={day.value} sx={{ borderRadius: '20px', border: '1px solid #edf2f7', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
                {/* Day Header */}
                <Box sx={{
                  p: 2, borderBottom: '1px solid #edf2f7',
                  background: 'linear-gradient(135deg, #263B7C 0%, #4C51BF 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                }}>
                  <Box>
                    <Typography variant="caption" fontWeight={800} color="rgba(255,255,255,0.7)" textTransform="uppercase" letterSpacing={1}>
                      {day.label.slice(0, 3)}
                    </Typography>
                    <Typography variant="body1" fontWeight={900} color="white">{day.label}</Typography>
                  </Box>
                  <Tooltip title={`Add slot for ${day.label}`}>
                    <IconButton size="small" onClick={() => openAddDialog(day.value)}
                      sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '8px', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Slots for the day */}
                <Box sx={{ p: 1.5, minHeight: 200 }}>
                  {daySlots.length === 0 ? (
                    <Box sx={{ py: 6, textAlign: 'center', opacity: 0.4 }}>
                      <EventNote sx={{ fontSize: 32, mb: 0.5 }} />
                      <Typography variant="caption" fontWeight={600} display="block">No classes</Typography>
                    </Box>
                  ) : (
                    <Stack spacing={1.5}>
                      {daySlots
                        .sort((a, b) => a.startTime.localeCompare(b.startTime))
                        .map((slot) => {
                          const isBreak = !!slot.title;
                          const color = isBreak ? '#718096' : (subjectColorMap[slot.subjectId] || '#4C51BF');
                          return (
                            <Paper key={slot.id} sx={{
                              p: 1.5, borderRadius: '14px', border: `1px solid ${color}25`,
                              bgcolor: isBreak ? '#F7FAFC' : `${color}08`, position: 'relative',
                              borderLeft: `4px solid ${color}`,
                              transition: 'all 0.2s',
                              '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.05)', transform: 'translateY(-1px)' },
                              '&:hover .slot-actions': { opacity: 1 }
                            }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                <Box flex={1}>
                                  <Typography variant="body2" fontWeight={800} color={color} sx={{ lineHeight: 1.2, mb: 0.5 }}>
                                    {isBreak ? slot.title : (slot.subject?.name || 'Subject')}
                                  </Typography>
                                  {!isBreak && (
                                    <Typography variant="caption" color="#4A5568" fontWeight={800}>
                                      {slot.subject?.code}
                                    </Typography>
                                  )}
                                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1 }}>
                                    <AccessTime sx={{ fontSize: 12, color: 'text.secondary' }} />
                                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                      {slot.startTime} – {slot.endTime}
                                    </Typography>
                                  </Stack>
                                </Box>
                                <Stack className="slot-actions" direction="row" sx={{ opacity: 0, transition: 'opacity 0.2s' }}>
                                  <Tooltip title="Edit">
                                    <IconButton size="small" sx={{ color: '#4C51BF', p: 0.5 }} onClick={() => openEditDialog(slot)}>
                                      <EditIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Remove">
                                    <IconButton size="small" sx={{ color: '#E53E3E', p: 0.5 }} onClick={() => handleDelete(slot.id)}>
                                      <DeleteIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </Stack>
                            </Paper>
                          );
                        })}
                    </Stack>
                  )}
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Subject Legend */}
      {!loading && slots.length > 0 && (
        <Paper sx={{ p: 3, borderRadius: '20px', border: '1px solid #edf2f7', mt: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
          <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase" sx={{ mb: 2, display: 'block' }}>Subject Legend</Typography>
          <Stack direction="row" flexWrap="wrap" gap={1.5}>
            {Array.from(new Set(slots.filter(s => !s.title).map(s => s.subjectId))).map(subjectId => {
              const subject = subjects.find(s => s.id === subjectId);
              const color = subjectColorMap[subjectId] || '#4C51BF';
              return subject ? (
                <Chip key={subjectId} label={`${subject.code} — ${subject.name}`} size="small"
                  sx={{ bgcolor: `${color}15`, color, fontWeight: 800, borderRadius: '8px', border: `1px solid ${color}30` }} />
              ) : null;
            })}
          </Stack>
        </Paper>
      )}

      {/* Add/Edit Slot Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#263B7C', pb: 1 }}>
          {editingSlot ? 'Edit Timetable Slot' : 'Add Timetable Slot'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 3 }}>
            <Stack spacing={2.5}>
              <Box sx={{ p: 1, bgcolor: '#F7FAFC', borderRadius: '12px', display: 'flex' }}>
                <Button 
                  fullWidth 
                  variant={formData.type === 'LESSON' ? 'contained' : 'text'}
                  onClick={() => setFormData({ ...formData, type: 'LESSON' })}
                  sx={{ borderRadius: '10px', fontWeight: 700, bgcolor: formData.type === 'LESSON' ? '#263B7C' : 'transparent', color: formData.type === 'LESSON' ? 'white' : '#718096' }}
                >
                  Lesson
                </Button>
                <Button 
                  fullWidth 
                  variant={formData.type === 'BREAK' ? 'contained' : 'text'}
                  onClick={() => setFormData({ ...formData, type: 'BREAK' })}
                  sx={{ borderRadius: '10px', fontWeight: 700, bgcolor: formData.type === 'BREAK' ? '#263B7C' : 'transparent', color: formData.type === 'BREAK' ? 'white' : '#718096' }}
                >
                  Break/Other
                </Button>
              </Box>

              <TextField select fullWidth label="Day" required
                value={formData.dayOfWeek}
                onChange={e => setFormData({ ...formData, dayOfWeek: Number(e.target.value) })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
                {DAYS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
              </TextField>

              {formData.type === 'LESSON' ? (
                <TextField select fullWidth label="Subject" required
                  value={formData.subjectId}
                  onChange={e => setFormData({ ...formData, subjectId: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
                  {subjects.map(s => <MenuItem key={s.id} value={s.id}>{s.code} — {s.name}</MenuItem>)}
                </TextField>
              ) : (
                <TextField 
                  fullWidth label="Title (e.g. Lunch Break)" required
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter break name..."
                  autoFocus
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  helperText="Common examples: Breakfast, Lunch, Assembly, Games"
                />
              )}

              <Stack direction="row" spacing={2}>
                <TextField fullWidth label="Start Time" type="time" required
                  InputLabelProps={{ shrink: true }}
                  value={formData.startTime}
                  onChange={e => setFormData({ ...formData, startTime: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                <TextField fullWidth label="End Time" type="time" required
                  InputLabelProps={{ shrink: true }}
                  value={formData.endTime}
                  onChange={e => setFormData({ ...formData, endTime: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={() => setOpenDialog(false)} sx={{ borderRadius: '10px', fontWeight: 600, color: '#718096' }}>Cancel</Button>
            <Button type="submit" variant="contained"
              sx={{ bgcolor: '#263B7C', borderRadius: '10px', px: 4, py: 1.2, fontWeight: 700, '&:hover': { bgcolor: '#1e2e60' } }}>
              {editingSlot ? 'Update Slot' : 'Add to Timetable'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default TimetablePage;
